import { useState, useEffect, lazy, Suspense } from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Hammer, Shield, Award, Home as HomeIcon, Wrench, FolderOpen, Info, HelpCircle, Calendar, Menu, X, ChevronLeft, ChevronRight, BookOpen, ChevronDown } from 'lucide-react';
import { updateSEO, pageSEO } from './utils/seo';

const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const DecksPage = lazy(() => import('./pages/DecksPage'));
const WorkProcessPage = lazy(() => import('./pages/WorkProcessPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const MaterialsFAQPage = lazy(() => import('./pages/MaterialsFAQPage'));
const ServicesFAQPage = lazy(() => import('./pages/ServicesFAQPage'));
const ProcessFAQPage = lazy(() => import('./pages/ProcessFAQPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ScheduleConsultationPage = lazy(() => import('./pages/ScheduleConsultationPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const ServiceAreaPage = lazy(() => import('./pages/ServiceAreaPage'));
const DeckAssessmentPage = lazy(() => import('./pages/DeckAssessmentPage'));

export type Page = 'home' | 'services' | 'service-decks' | 'process' | 'portfolio' | 'about' | 'faq' | 'materials-faq' | 'services-faq' | 'process-faq' | 'contact' | 'schedule' | 'thank-you' | 'blog' | 'service-area' | 'deck-assessment' | `blog-post:${string}` | `location:${string}`;

function App() {
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    const path = window.location.pathname.slice(1);
    if (path === 'our-process') return 'process';
    if (path === 'services/decks') return 'service-decks';
    if (path === 'services/patios' || path === 'services/outdoor-kitchens') return 'services';
    if (path === 'blog') return 'blog';
    if (path === 'service-area') return 'service-area';
    if (path === 'get-your-deck') return 'deck-assessment';
    if (path.startsWith('blog/')) return `blog-post:${path.slice(5)}` as Page;
    if (path.startsWith('locations/')) return `location:${path.slice(10)}` as Page;
    return (path as Page) || 'home';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);

  useEffect(() => {
    // Blog post SEO is handled inside BlogPostPage
    if (currentPage.startsWith('blog-post:')) return;
    if (currentPage === 'deck-assessment') return;
    const seoConfig = pageSEO[currentPage];
    if (seoConfig) {
      updateSEO(seoConfig);
    }
  }, [currentPage]);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.slice(1);
      if (path === 'our-process') {
        setCurrentPage('process');
      } else if (path === 'services/decks') {
        setCurrentPage('service-decks');
      } else if (path === 'services/patios' || path === 'services/outdoor-kitchens') {
        setCurrentPage('services');
      } else if (path === 'blog') {
        setCurrentPage('blog');
      } else if (path === 'service-area') {
        setCurrentPage('service-area');
      } else if (path === 'get-your-deck') {
        setCurrentPage('deck-assessment');
      } else if (path.startsWith('blog/')) {
        setCurrentPage(`blog-post:${path.slice(5)}` as Page);
      } else if (path.startsWith('locations/')) {
        setCurrentPage(`location:${path.slice(10)}` as Page);
      } else {
        setCurrentPage((path as Page) || 'home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const renderPage = () => {
    const page = (() => {
      switch (currentPage) {
        case 'home':
          return <HomePage onNavigate={handleNavigation} />;
        case 'services':
          return <ServicesPage onNavigate={handleNavigation} />;
        case 'service-decks':
          return <DecksPage onNavigate={handleNavigation} />;
        case 'process':
          return <WorkProcessPage onNavigate={handleNavigation} />;
        case 'portfolio':
          return <PortfolioPage onNavigate={handleNavigation} />;
        case 'about':
          return <AboutPage />;
        case 'faq':
          return <FAQPage onNavigate={handleNavigation} />;
        case 'materials-faq':
          return <MaterialsFAQPage onNavigate={handleNavigation} />;
        case 'services-faq':
          return <ServicesFAQPage onNavigate={handleNavigation} />;
        case 'process-faq':
          return <ProcessFAQPage onNavigate={handleNavigation} />;
        case 'contact':
          return <ContactPage onNavigate={handleNavigation} />;
        case 'schedule':
          return <ScheduleConsultationPage />;
        case 'thank-you':
          return <ThankYouPage onNavigate={handleNavigation} />;
        case 'blog':
          return <BlogIndexPage onNavigate={handleNavigation} />;
        case 'service-area':
          return <ServiceAreaPage onNavigate={handleNavigation} />;
        case 'deck-assessment':
          return <DeckAssessmentPage onNavigate={handleNavigation} />;
        default:
          if (currentPage.startsWith('blog-post:')) {
            return <BlogPostPage slug={currentPage.slice(10)} onNavigate={handleNavigation} />;
          }
          if (currentPage.startsWith('location:')) {
            return <LocationPage slug={currentPage.slice(9)} onNavigate={handleNavigation} />;
          }
          return <HomePage onNavigate={handleNavigation} />;
      }
    })();

    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-crm-orange border-t-transparent rounded-full animate-spin" /></div>}>
        {page}
      </Suspense>
    );
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (pendingScrollId) {
      const el = document.getElementById(pendingScrollId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
      }
      setPendingScrollId(null);
    }
  }, [currentPage, pendingScrollId]);

  const handleNavigation = (page: Page, scrollId?: string) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    let url: string;
    if (page === 'home') url = '';
    else if (page === 'process') url = 'our-process';
    else if (page === 'service-decks') url = 'services/decks';
    else if (page === 'blog') url = 'blog';
    else if (page === 'service-area') url = 'service-area';
    else if (page.startsWith('blog-post:')) url = `blog/${page.slice(10)}`;
    else if (page.startsWith('location:')) url = `locations/${page.slice(9)}`;
    else url = page;
    window.history.pushState({}, '', `/${url}`);
    if (scrollId) {
      setPendingScrollId(scrollId);
    } else {
      scrollToTop();
    }
  };

  return (
    <div className="min-h-screen bg-crm-bg flex">
      {/* Left Sidebar Navigation */}
      <aside className={`hidden lg:flex lg:flex-col bg-white border-r border-crm-gray-200 fixed h-screen transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        {/* Logo */}
        <div className={`border-b border-crm-gray-200 flex items-center justify-between ${
          sidebarCollapsed ? 'p-3' : 'p-4'
        }`}>
          {!sidebarCollapsed && (
            <button onClick={() => handleNavigation('home')} className="flex-shrink-0">
              <img src="/optimized/nj4-without_slogan-final.webp" alt="NJ Carpenter LLC" className="block h-8 w-auto" width="400" height="72" loading="eager" />
            </button>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-crm-gray-50 rounded text-crm-gray-600 hover:text-crm-gray-800 transition-colors ml-auto"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto p-3">
          <div className="space-y-0.5">
            <button
              onClick={() => handleNavigation('home')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'home'
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'Home' : ''}
            >
              {currentPage === 'home' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <HomeIcon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Home</span>}
            </button>

            {/* Services group */}
            {!sidebarCollapsed ? (
              <>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                    ['service-decks', 'process', 'services'].includes(currentPage)
                      ? 'text-crm-gray-800 bg-crm-gray-50'
                      : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
                  }`}
                >
                  {['service-decks', 'process', 'services'].includes(currentPage) && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
                  <Wrench className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left">Services</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="ml-8 space-y-0.5">
                    <button
                      onClick={() => handleNavigation('service-decks')}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-xs font-medium transition-all relative ${
                        currentPage === 'service-decks'
                          ? 'text-crm-orange bg-crm-gray-50'
                          : 'text-crm-gray-500 hover:bg-crm-gray-50 hover:text-crm-gray-800'
                      }`}
                    >
                      <Wrench className="w-4 h-4 flex-shrink-0" />
                      <span>Decks</span>
                    </button>
                    <button
                      onClick={() => handleNavigation('process')}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-xs font-medium transition-all relative ${
                        currentPage === 'process'
                          ? 'text-crm-orange bg-crm-gray-50'
                          : 'text-crm-gray-500 hover:bg-crm-gray-50 hover:text-crm-gray-800'
                      }`}
                    >
                      <Hammer className="w-4 h-4 flex-shrink-0" />
                      <span>Our Process</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={() => handleNavigation('services')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                  ['service-decks', 'process', 'services'].includes(currentPage)
                    ? 'text-crm-gray-800 bg-crm-gray-50'
                    : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
                }`}
                title="Services"
              >
                {['service-decks', 'process', 'services'].includes(currentPage) && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
                <Wrench className="w-5 h-5 flex-shrink-0" />
              </button>
            )}

            <button
              onClick={() => handleNavigation('portfolio')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'portfolio'
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'Portfolio' : ''}
            >
              {currentPage === 'portfolio' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <FolderOpen className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Portfolio</span>}
            </button>

            <button
              onClick={() => handleNavigation('about')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'about'
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'About' : ''}
            >
              {currentPage === 'about' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <Info className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>About</span>}
            </button>

            <button
              onClick={() => handleNavigation('faq')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'faq'
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'FAQ' : ''}
            >
              {currentPage === 'faq' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>FAQ</span>}
            </button>

            <button
              onClick={() => handleNavigation('blog')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'blog' || currentPage.startsWith('blog-post:')
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'Blog' : ''}
            >
              {(currentPage === 'blog' || currentPage.startsWith('blog-post:')) && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <BookOpen className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Blog</span>}
            </button>

            <button
              onClick={() => handleNavigation('service-area')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'service-area' || currentPage.startsWith('location:')
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'Service Area' : ''}
            >
              {(currentPage === 'service-area' || currentPage.startsWith('location:')) && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <MapPin className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Service Area</span>}
            </button>

            <button
              onClick={() => handleNavigation('schedule')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all relative ${
                currentPage === 'schedule'
                  ? 'text-crm-gray-800 bg-crm-gray-50'
                  : 'text-crm-gray-600 hover:bg-crm-gray-50 hover:text-crm-gray-800'
              }`}
              title={sidebarCollapsed ? 'Schedule Consultation' : ''}
            >
              {currentPage === 'schedule' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-crm-orange"></div>}
              <Calendar className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Schedule Consultation</span>}
            </button>
          </div>
        </nav>

        {/* Contact Info */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-crm-gray-200 space-y-3">
            <a
              href="tel:609-517-2265"
              className="flex items-center gap-2 text-sm text-crm-gray-600 hover:text-crm-orange transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>(609) 517-2265</span>
            </a>
            <a
              href="https://crew.thenjcarpenter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-[11px] text-crm-gray-300 hover:text-crm-gray-500 transition-colors"
            >
              Field Crew
            </a>
          </div>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-crm-gray-200 z-50">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => handleNavigation('home')}>
            <img src="/optimized/nj4-without_slogan-final.webp" alt="NJ Carpenter LLC" className="h-9 w-auto" width="400" height="72" loading="eager" />
          </button>
          <div className="flex items-center gap-4">
            <a
              href="https://crew.thenjcarpenter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-crm-gray-300 hover:text-crm-gray-500 transition-colors"
            >
              Field Crew
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-crm-gray-600"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-crm-gray-200 bg-white max-h-screen overflow-y-auto">
            <nav className="p-4 space-y-1">
              <button onClick={() => handleNavigation('home')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <HomeIcon className="w-5 h-5" />
                <span>Home</span>
              </button>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded"
              >
                <Wrench className="w-5 h-5" />
                <span className="flex-1 text-left">Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="ml-8 space-y-1">
                  <button onClick={() => handleNavigation('service-decks')} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-crm-gray-500 hover:bg-crm-gray-50 rounded">
                    <Wrench className="w-4 h-4" />
                    <span>Decks</span>
                  </button>
                  <button onClick={() => handleNavigation('process')} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-crm-gray-500 hover:bg-crm-gray-50 rounded">
                    <Hammer className="w-4 h-4" />
                    <span>Our Process</span>
                  </button>
                </div>
              )}
              <button onClick={() => handleNavigation('portfolio')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <FolderOpen className="w-5 h-5" />
                <span>Portfolio</span>
              </button>
              <button onClick={() => handleNavigation('about')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <Info className="w-5 h-5" />
                <span>About</span>
              </button>
              <button onClick={() => handleNavigation('faq')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <HelpCircle className="w-5 h-5" />
                <span>FAQ</span>
              </button>
              <button onClick={() => handleNavigation('blog')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <BookOpen className="w-5 h-5" />
                <span>Blog</span>
              </button>
              <button onClick={() => handleNavigation('service-area')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-crm-gray-600 hover:bg-crm-gray-50 rounded">
                <MapPin className="w-5 h-5" />
                <span>Service Area</span>
              </button>
              <button onClick={() => handleNavigation('schedule')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium bg-crm-orange text-white hover:bg-crm-orange-dark rounded">
                <Calendar className="w-5 h-5" />
                <span>Schedule Consultation</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="pt-16 lg:pt-0">
          <main>
            {renderPage()}
          </main>

          {/* Footer */}
          <footer className="bg-crm-gray-50 border-t border-crm-gray-200 mt-12">
            <div className="max-w-7xl mx-auto px-6 py-12">
              <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-10">
                {/* Contact Info */}
                <div>
                  <h2 className="font-bold text-crm-gray-800 mb-5 text-sm uppercase tracking-wide">Contact</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-crm-orange mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-crm-gray-600 leading-relaxed">16 Robin Rd, Egg Harbor Township, NJ 08234</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-crm-orange flex-shrink-0" />
                      <a href="tel:609-517-2265" className="text-sm text-crm-gray-700 hover:text-crm-orange font-semibold transition-colors">
                        (609) 517-2265
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-crm-orange flex-shrink-0" />
                      <a href="mailto:info@thenjcarpenter.com" className="text-sm text-crm-orange hover:text-crm-orange-dark transition-colors">
                        info@thenjcarpenter.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Credentials */}
                <div>
                  <h2 className="font-bold text-crm-gray-800 mb-5 text-sm uppercase tracking-wide">Credentials</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-crm-orange mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-crm-gray-700">Licensed & Insured</p>
                        <p className="text-xs text-crm-gray-500 mt-0.5">#13VH13299500</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-crm-orange mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-crm-gray-700">TimberTech PRO</p>
                        <p className="text-xs text-crm-gray-500 mt-0.5">Certified Professional</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Certifications */}
                <div>
                  <h2 className="font-bold text-crm-gray-800 mb-5 text-sm uppercase tracking-wide">Certifications</h2>
                  <div className="flex flex-col gap-5">
                    <a
                      href="https://www.bbb.org/us/nj/egg-harbor-township/profile/outdoor-living/nj-carpenter-llc-0221-90240181/#sealclick"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-block hover:opacity-80 transition-opacity max-w-[160px]"
                    >
                      <img
                        src="https://seal-newjersey.bbb.org/seals/blue-seal-293-61-bbb-90240181.png"
                        alt="NJ Carpenter LLC BBB Business Review"
                        width="293"
                        height="61"
                        loading="lazy"
                        className="w-full h-auto"
                      />
                    </a>
                    <div className="max-w-[110px]">
                      <img src="/optimized/BoardProSilverpng1.webp"
                        alt="TimberTech PRO Certified Professional"
                        className="w-full h-auto object-contain" width="300" height="256" loading="eager" />
                    </div>
                  </div>
                </div>

                {/* Social */}
                <div>
                  <h2 className="font-bold text-crm-gray-800 mb-5 text-sm uppercase tracking-wide">Follow Us</h2>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.facebook.com/njcarpenterllc"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit NJ Carpenter on Facebook"
                      className="w-11 h-11 bg-white border border-crm-gray-200 rounded-lg flex items-center justify-center text-crm-gray-600 hover:bg-crm-orange hover:text-white hover:border-crm-orange transition-all shadow-sm"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://instagram.com/thenjcarpenter"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit NJ Carpenter on Instagram"
                      className="w-11 h-11 bg-white border border-crm-gray-200 rounded-lg flex items-center justify-center text-crm-gray-600 hover:bg-crm-orange hover:text-white hover:border-crm-orange transition-all shadow-sm"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.youtube.com/@NJCARPENTERLLC"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit NJ Carpenter on YouTube"
                      className="w-11 h-11 bg-white border border-crm-gray-200 rounded-lg flex items-center justify-center text-crm-gray-600 hover:bg-crm-orange hover:text-white hover:border-crm-orange transition-all shadow-sm"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Bottom Bar */}
              <div className="border-t border-crm-gray-200 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-3 text-sm">
                <p className="text-crm-gray-500">© {new Date().getFullYear()} NJ Carpenter LLC. All rights reserved.</p>
                <p className="text-crm-gray-600">
                  Powered by{' '}
                  <a
                    href="https://lanzalo.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-crm-orange hover:text-crm-orange-dark font-bold transition-colors"
                  >
                    Lanzalo
                  </a>
                </p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;
